import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { SSLogoFull, SSLogoIcon } from '../components/SSLogo';
import { 
  Home, Store, Package, ShoppingBag, Receipt, LayoutDashboard, 
  GraduationCap, HelpCircle, Settings, LogOut, LogIn, Info,
  TrendingUp, Sparkles, Warehouse, ListTodo, Megaphone, Truck
} from 'lucide-react';

const LEVEL_TIERS = [
  { level: 1, name: "Bronze", name_ar: "برونزي", min_points: 0, commission_rate: 0.03, color: "#CD7F32" },
  { level: 2, name: "Silver", name_ar: "فضي", min_points: 100, commission_rate: 0.05, color: "#C0C0C0" },
  { level: 3, name: "Gold", name_ar: "ذهبي", min_points: 500, commission_rate: 0.08, color: "#FFD700" },
  { level: 4, name: "Platinum", name_ar: "بلاتيني", min_points: 2000, commission_rate: 0.12, color: "#A0B2C6" },
  { level: 5, name: "Diamond", name_ar: "ماسي", min_points: 5000, commission_rate: 0.15, color: "#40B5E0" },
  { level: 6, name: "Elite", name_ar: "نخبة", min_points: 15000, commission_rate: 0.20, color: "#2DD4BF" },
];

function getNextLevel(currentLevel) {
  const idx = LEVEL_TIERS.findIndex(t => t.level === currentLevel);
  if (idx >= 0 && idx + 1 < LEVEL_TIERS.length) return LEVEL_TIERS[idx + 1];
  return null;
}

export default function Sidebar({ open, onClose, collapsed, onToggleCollapse }) {
  const { t, isRTL } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', label: isRTL ? 'الرئيسية' : 'Home', icon: Home },
    { path: '/products', label: isRTL ? 'المنتجات' : 'Products', icon: Package },
    { path: '/warehouses', label: isRTL ? 'مستودعاتنا' : 'Our Warehouses', icon: Warehouse },
    { path: '/tasks', label: isRTL ? 'المهام' : 'Tasks', icon: ListTodo, auth: true },
    { path: '/marketing-guide', label: isRTL ? 'دليل التسويق' : 'Marketing Guide', icon: Megaphone },
    { path: '/my-store', label: isRTL ? 'متجري' : 'My Store', icon: ShoppingBag, auth: true },
    { path: '/shipments', label: isRTL ? 'تتبع الشحنات' : 'Track Shipments', icon: Truck, auth: true },
    { path: '/orders', label: isRTL ? 'الطلبات' : 'Orders', icon: Receipt, auth: true },
    { path: '/dashboard', label: isRTL ? 'حسابي' : 'My Account', icon: LayoutDashboard, auth: true },
    { path: '/ai-seller', label: isRTL ? 'البائع الذكي' : 'AI Seller', icon: Sparkles, auth: true },
    { path: '/tutorials', label: isRTL ? 'الدروس' : 'Tutorials', icon: GraduationCap },
    { path: '/support', label: isRTL ? 'الدعم' : 'Support', icon: HelpCircle },
    { path: '/about', label: isRTL ? 'من نحن' : 'About Us', icon: Info },
    { path: '/settings', label: isRTL ? 'الإعدادات' : 'Settings', icon: Settings, auth: true },
  ];

  const NavContent = ({ isCollapsed = false }) => {
    const userLevel = user?.level?.level || 1;
    const userPoints = user?.total_points || 0;
    const nextLevel = getNextLevel(userLevel);
    const pointsNeeded = nextLevel ? nextLevel.min_points - userPoints : 0;
    const progressPercent = nextLevel 
      ? Math.min(100, Math.round((userPoints / nextLevel.min_points) * 100)) 
      : 100;

    return (
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={`${isCollapsed ? 'p-3 flex justify-center' : 'p-5'}`}>
          {isCollapsed ? <SSLogoIcon size={32} /> : <SSLogoFull />}
        </div>
        <Separator className="mb-2" />

        {/* User card */}
        {isAuthenticated && user && !isCollapsed && (
          <div className="px-4 py-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">{user.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3 text-primary" />
                  <span className="text-xs text-muted-foreground">{isRTL ? user.level?.name_ar : user.level?.name || 'Bronze'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {isAuthenticated && user && isCollapsed && (
          <div className="px-2 py-3 flex justify-center">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center" title={user.name}>
              <span className="text-sm font-semibold text-primary">{user.name?.charAt(0)?.toUpperCase()}</span>
            </div>
          </div>
        )}

        {/* Level progress notification */}
        {isAuthenticated && user && nextLevel && !isCollapsed && (
          <div className="px-4 pb-2">
            <Link to="/tasks" onClick={onClose} data-testid="level-progress-banner">
              <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium" style={{ color: nextLevel.color }}>
                    {isRTL ? nextLevel.name_ar : nextLevel.name}
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-1.5 mb-1.5" />
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {isRTL 
                    ? `باقي ${pointsNeeded} نقطة للمستوى التالي (${Math.round(nextLevel.commission_rate * 100)}% عمولة)`
                    : `${pointsNeeded} pts to next level (${Math.round(nextLevel.commission_rate * 100)}% commission)`
                  }
                </p>
              </div>
            </Link>
          </div>
        )}

        {/* Nav items - hidden scrollbar */}
        <nav className={`flex-1 ${isCollapsed ? 'px-2' : 'px-3'} py-2 space-y-1 overflow-y-auto sidebar-nav-scroll`}>
          {navItems.map((item) => {
            if (item.auth && !isAuthenticated) return null;
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                title={isCollapsed ? item.label : undefined}
                className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span>{item.label}</span>
                    {item.path === '/orders' && isAuthenticated && user?.total_sales > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs bg-primary/20 text-primary">{user.total_sales}</Badge>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        <Separator className="mt-2" />

        {/* Bottom actions */}
        <div className={`${isCollapsed ? 'px-2' : 'px-3'} py-2`}>
          {isAuthenticated ? (
            <Button
              variant="ghost"
              className={`w-full ${isCollapsed ? 'justify-center px-2' : 'justify-start gap-3'} text-muted-foreground hover:text-destructive`}
              onClick={() => { logout(); onClose?.(); }}
              data-testid="nav-logout"
              title={isCollapsed ? (isRTL ? 'تسجيل الخروج' : 'Logout') : undefined}
            >
              <LogOut className="w-5 h-5" />
              {!isCollapsed && <span>{isRTL ? 'تسجيل الخروج' : 'Logout'}</span>}
            </Button>
          ) : (
            <Link to="/login" onClick={onClose}>
              <Button
                variant="ghost"
                className={`w-full ${isCollapsed ? 'justify-center px-2' : 'justify-start gap-3'} text-muted-foreground hover:text-primary`}
                data-testid="nav-login"
                title={isCollapsed ? (isRTL ? 'تسجيل الدخول' : 'Login') : undefined}
              >
                <LogIn className="w-5 h-5" />
                {!isCollapsed && <span>{isRTL ? 'تسجيل الدخول' : 'Login'}</span>}
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        className={`hidden lg:block fixed top-0 bottom-0 border-border bg-card/95 backdrop-blur-sm z-40 ${isRTL ? 'right-0 border-l' : 'left-0 border-r'}`}
        animate={{ width: collapsed ? 64 : 288 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        <NavContent isCollapsed={collapsed} />
        <EdgeToggleButton collapsed={collapsed} onClick={onToggleCollapse} isRTL={isRTL} />
      </motion.aside>

      {/* Mobile Sidebar */}
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side={isRTL ? 'right' : 'left'} className="w-72 p-0 bg-card overflow-x-hidden">
          <NavContent isCollapsed={false} />
        </SheetContent>
      </Sheet>
    </>
  );
}

function EdgeToggleButton({ collapsed, onClick, isRTL }) {
  return (
    <button
      onClick={onClick}
      data-testid="sidebar-toggle"
      className={`absolute top-1/2 -translate-y-1/2 z-50 w-7 h-14 flex items-center justify-center cursor-pointer outline-none hover:scale-110 active:scale-90 transition-transform ${isRTL ? '-left-3.5' : '-right-3.5'}`}
    >
      <div className="relative w-7 h-14 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-b from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30">
        <span className="relative z-10 text-black text-xs font-bold">
          {isRTL ? (collapsed ? '→' : '←') : (collapsed ? '→' : '←')}
        </span>
      </div>
    </button>
  );
}
